
import React, { useMemo, useState } from "react";
import IMAGES from "../../../../assets/images/images";
import styles from "./serviceDetail.module.css";
import { formatDate } from "../../../../utils/arrayUtils";
import { getLogo } from "../../../../utils/constants/fileTypeLogo";
import { capitalizeWords } from "../../../../utils/utils";
import SecondaryTooltip from "../../../../components/secondaryToolTip/secondaryToolTip";
import { CATEGORY, selectionType } from "../addDemandTemplate/demandHelper";

const Previousconfiguration = ({ data }) => {
  return (
    <div className={styles.previousservice}>
      <div className={styles.servicecount}>Previous Configuration</div>
      <div className={styles.PreviousHeader}></div>
      <div className={styles.servicedetailwrapper}>
        <div className={styles.servicedetail}>
          <div className={styles.data}>
            <span className={styles.datalabel}>VLAN ID:</span>
            <span className={styles.datavalue}>{data?.vlan || "—"}</span>
          </div>
          <div className={styles.data}>
            <span className={styles.datalabel}>Router City:</span>
            <span className={styles.datavalue}>{data?.router_location || "—"}</span>
          </div>
          <div className={styles.data}>
            <span className={styles.datalabel}>Router Name:</span>
            <span className={styles.datavalue}>{data?.pe_ref || "—"}</span>
          </div>
          <div className={styles.data}>
            <span className={styles.datalabel}>Allocated Capacity (Mbps):</span>
            <span className={styles.datavalue}>
              {data?.logical_capacity_mbps ?? "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

const ExpandableComment = ({ value, previewChars = 100, emptyText = "---", collapsedClassName, expandedClassName, lineClassName, viewMoreClassName, }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const comment = (value ?? "").toString();
  const hasValue = comment.trim().length > 0;
  const computed = useMemo(() => {
    if (!hasValue) {
      return { showViewMore: false, hasNewline: false, previewText: emptyText, previewLines: [], fullLines: [], };
    }
    const hasNewline = comment.includes("\n");
    const firstNewlineIndex = comment.indexOf("\n");
    const previewText = firstNewlineIndex !== -1 ? comment.slice(0, firstNewlineIndex) : comment.slice(0, previewChars);
    const previewLines = previewText.split("\n");
    const fullLines = comment.split("\n");
    const showViewMore = comment.length > previewChars || hasNewline;
    return { showViewMore, hasNewline, previewText, previewLines, fullLines };

  }, [comment, hasValue, previewChars, emptyText]);

  if (!hasValue) return <span>{emptyText}</span>;

  const renderLines = (lines) =>
    lines.map((line, i) =>
      i === lines.length - 1 ? (
        <span className={lineClassName} key={i}>{line}</span>
      ) : (
        <div className={lineClassName} key={i}>{line}</div>
      )
    );

  return (
    <div className={styles.name}>
      <div className={isExpanded ? expandedClassName : collapsedClassName}>
        {isExpanded ? (
          renderLines(computed.fullLines)
        ) : computed.hasNewline ? (
          <span className={lineClassName}>{computed.previewText}</span>
        ) : (
          renderLines(computed.previewLines)
        )}
        {computed.showViewMore && (
          <span
            className={viewMoreClassName}
            onClick={() => setIsExpanded((v) => !v)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setIsExpanded((v) => !v)}
          >
            {isExpanded ? " View Less" : " View More"}
          </span>
        )}
      </div>
    </div>
  );
}

const ServiceDetail = ({ data, onFileDownload }) => {
  const { tadigId, requestId, createdBy, createdAt, services = [] } = data || {};
  const [isOpen, setIsOpen] = useState(false);

  const downloadFile = (file, index, onDownload) => {
    onDownload(file)
  }

  return (
    <>
      <div className={styles.parent}>
        <div className={styles.wraper}>
          <div className={styles.detail}>
            <span className={styles.label}>TADIG Code: </span>
            <span className={styles.value}>{tadigId || "---"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.label}>Demand ID: </span>
            <span className={styles.value}>{requestId || "---"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.label}>Requester: </span> 
            <SecondaryTooltip copy={true} text={createdBy?.email} positions={['bottom', 'right']}>
            <span className={styles.value}>{capitalizeWords(createdBy?.name) || "---"}</span></SecondaryTooltip>
          </div>
          <div className={styles.detail}>
            <span className={styles.label}>Submitted On: </span>
            <span className={styles.value}>
              {createdAt ? formatDate(createdAt) : "---"}
            </span>
          </div>
        </div>

        {/* Toggle */}
        <button type="button" className={styles.arrow} onClick={() => setIsOpen((o) => !o)}
          title={isOpen ? "Collapse services" : "Expand services"}
        >
          <img
            className={styles.uparrow}
            src={IMAGES.arrowdown}
            alt={isOpen ? "Collapse services" : "Expand services"}
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </button>
      </div>

      {/* Accordion Content */}
      <div
        id="services-accordion"
        className={`${styles.acordian} ${isOpen ? styles.open : styles.closed}`}
      >
        {!Array.isArray(services) ||services?.length === 0 ? (
          <div className={styles.empty}>No services available.</div>
        ) : (
          services?.map((svc, idx) => (
            <div className={styles.servicewrapper}>
              <div key={`${svc?.service || "service"}-${idx}`} className={styles.service}>
                <div className={styles.servicecount}>Service {idx + 1}</div>
                <div className={styles.servicedetailwrapper}>
                  <div className={styles.servicedetail}>
                    <div className={styles.data}>
                      <span className={styles.datalabel}>Service:</span>
                      <span className={styles.datavalue}>{svc?.service || "—"}</span>
                    </div>
                    <div className={styles.data}>
                      <span className={styles.datalabel}>Region:</span>
                      <span className={styles.datavalue}>{svc?.serviceRegion?.label || "—"}</span>
                    </div>
                    <div className={styles.data}>
                      <span className={styles.datalabel}>Country:</span>
                      <span className={styles.datavalue}>{svc?.serviceCountry?.label || "—"}</span>
                    </div>
                    {svc?.service_option?.avg_monthly_dtr_volumes && <div className={styles.data}>
                      <span className={styles.datalabel}>Avg. Monthly DTR Volumes:</span>
                      <span className={styles.datavalue}>
                        {svc?.service_option?.avg_monthly_dtr_volumes ?? "—"}
                      </span>
                    </div>}
                    {svc?.service_option?.dns_number && <div className={styles.data}>
                      <span className={styles.datalabel}>How Many DNS:</span>
                      <span className={styles.datavalue}>
                        {svc?.service_option?.dns_number ?? "—"}
                      </span>
                    </div>}
                    {svc?.service_option?.type && <div className={styles.data}>
                      <span className={styles.datalabel}>Type:</span>
                      <span className={styles.datavalue}>
                        {svc?.service_option?.type.join(", ") ?? "—"}
                      </span>
                    </div>}
                    {svc?.service_option?.avg_monthly_msu_volumes && <div className={styles.data}>
                      <span className={styles.datalabel}>Avg Monthly MSU Volumes:</span>
                      <span className={styles.datavalue}>
                        {svc?.service_option?.avg_monthly_msu_volumes ?? "—"}
                      </span>
                    </div>}
                    {svc?.service_option?.top_destinations && <div className={styles.data}>
                      <span className={styles.datalabel}>Top Destinations:</span>
                      <span className={styles.datavalue}>
                        {svc?.service_option?.top_destinations ?? "—"}
                      </span>
                    </div>}
                    {svc?.service_option?.allocated_capacity && <div className={styles.data}>
                      <span className={styles.datalabel}>Allocated Capacity:</span>
                      <span className={styles.datavalue}>
                        {svc?.service_option?.allocated_capacity ?? "—"}
                      </span>
                    </div>}
                    {svc?.service_option?.category && <div className={styles.data}>
                      <span className={styles.datalabel}>Category:</span>
                      <span className={styles.datavalue}>
                        {svc?.service_option?.category?.map(value =>Object.values(CATEGORY).find(category => category.value === value)?.label || value).join(", ") || "—"}
                      </span>
                    </div>}
                     {svc?.service_option?.avg_mon_trans_volume && <div className={styles.data}>
                      <span className={styles.datalabel}>Average Monthly Transaction Volumes (TTR):</span>
                      <span className={styles.datavalue}>
                        {svc?.service_option?.avg_mon_trans_volume ?? "—"}
                      </span>
                    </div>}
                     {svc?.service_option?.fiveG_Core && <div className={styles.data}>
                      <span className={styles.datalabel}>Do you have a 5G Core:</span>
                      <span className={styles.datavalue}>
                        {selectionType?.find(item => item.value === svc?.service_option?.fiveG_Core)?.label || "—"}
                      </span>
                    </div>}
                     {svc?.service_option?.fiveG_SA_domestically && <div className={styles.data}>
                      <span className={styles.datalabel}>Have you launched 5G SA domestically:</span>
                      <span className={styles.datavalue}>
                        {selectionType?.find(item => item.value === svc?.service_option?.fiveG_SA_domestically)?.label || "—"}
                      </span>
                    </div>}
                     {svc?.service_option?.hosted_Sepp_at_vodafone && <div className={styles.data}>
                      <span className={styles.datalabel}>Do you require a hosted SePP at Vodafone:</span>
                      <span className={styles.datavalue}>
                        {selectionType?.find(item => item.value === svc?.service_option?.hosted_Sepp_at_vodafone)?.label || "—"}
                      </span>
                    </div>}
                  </div>
                  <div>
                    {svc?.files?.length > 0 && <div  className={styles.data}>
                      <div className={styles.label}>Attachments  :</div>
                      <div className={`${styles.attachmentwrapper}`}>
                        {svc?.files?.length > 0 ?
                          <div className={styles.attachmentCont}>
                            {svc?.files?.map((item, index) =>
                              <div className={styles.attachment} key={index} title={item?.originalFileName}>
                                <img className={styles.attachlogos} src={getLogo(item?.originalFileName)} />
                                <span className={styles.filename}>{item?.originalFileName}</span>
                                <img className={styles.attachlog} src={IMAGES.downloadlogo} onClick={() => downloadFile(item, index, onFileDownload)} />
                              </div>)}
                          </div>
                          : ''}
                      </div></div>}
                    <div>
                      {svc?.comment && <div className={styles.data}><div className={styles.label}>Additional Information:</div>
                        <ExpandableComment
                          value={svc?.comment}
                          previewChars={100}
                          emptyText="---"
                          collapsedClassName={styles.infotexts}
                          expandedClassName={styles.infotext}
                          lineClassName={styles.info}
                          viewMoreClassName={styles.viewMore}
                        /></div>}
                    </div>
                  </div>
                </div>
              </div>
              {svc?.additional_info && Object.keys(svc?.additional_info).length > 0 ? (
                <Previousconfiguration data={svc?.additional_info} />
              ) : <></>}
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default ServiceDetail;